/**
 * The Venues list keeps its filters, sort and page in the URL so a filtered list can be linked
 * to (`/venues?country=US&state=AZ`) and survives reloads. URL keys are snake_case; API params
 * are camelCase. Defaults are left out of the URL. Contract: DIVE-6877 spec §3.1 and §3.3.
 */

export const DEFAULT_SIZE = 25;
const MAX_SIZE = 200;

export const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "ALL", label: "All" },
];

/** Per-column "contains" searches (plus the exact `id`); the page debounces these as drafts. */
export const SEARCH_KEYS = [
  "id",
  "name",
  "street",
  "city",
  "county",
  "postalCode",
  "timezone",
];

export const DEFAULT_STATE = {
  id: "",
  name: "",
  street: "",
  city: "",
  county: "",
  postalCode: "",
  timezone: "",
  country: "",
  state: "",
  status: "ACTIVE",
  missingTimezone: false,
  missingMapLink: false,
  sort: "Id",
  isAscending: true,
  page: 0,
  size: DEFAULT_SIZE,
};

/** state key -> URL key, for the plain string filters. */
const URL_KEYS = {
  id: "id",
  name: "name",
  street: "street",
  city: "city",
  county: "county",
  postalCode: "postal_code",
  timezone: "timezone",
  country: "country",
  state: "state",
};

/** state key -> URL key, for the checkbox filters (`1` when on, absent when off). */
const FLAG_KEYS = {
  missingTimezone: "missing_tz",
  missingMapLink: "missing_map",
};

const STATUSES = new Set(STATUS_OPTIONS.map((o) => o.value));

const nonNegativeInt = (value, fallback) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : fallback;
};

const isOn = (value) => value === "1" || value.toLowerCase() === "true";

/**
 * Parses `location.search`. Invalid values fall back to the default instead of erroring, so a
 * mistyped link still opens the list. A state without a country is dropped: the State filter
 * is chosen from the selected country's list.
 */
export const readQuery = (search) => {
  const sp = new URLSearchParams(search);
  const get = (key) => (sp.get(key) || "").trim();

  const result = { ...DEFAULT_STATE };
  Object.entries(URL_KEYS).forEach(([stateKey, urlKey]) => {
    result[stateKey] = get(urlKey);
  });
  Object.entries(FLAG_KEYS).forEach(([stateKey, urlKey]) => {
    result[stateKey] = isOn(get(urlKey));
  });

  if (!/^\d+$/.test(result.id)) result.id = "";
  result.country = result.country.toUpperCase();
  result.state = result.country ? result.state.toUpperCase() : "";

  const status = get("status").toUpperCase();
  result.status = STATUSES.has(status) ? status : DEFAULT_STATE.status;

  result.sort = get("sort") || DEFAULT_STATE.sort;
  result.isAscending = get("dir").toLowerCase() !== "desc";
  result.page = nonNegativeInt(get("page"), 0);
  const size = nonNegativeInt(get("size"), DEFAULT_SIZE);
  result.size = size > 0 && size <= MAX_SIZE ? size : DEFAULT_SIZE;
  return result;
};

/** The URL search string for `state`, defaults omitted (`""` when nothing is set). */
export const writeQuery = (state) => {
  const sp = new URLSearchParams();
  Object.entries(URL_KEYS).forEach(([stateKey, urlKey]) => {
    const value = String(state[stateKey] ?? "").trim();
    if (value) sp.set(urlKey, value);
  });
  Object.entries(FLAG_KEYS).forEach(([stateKey, urlKey]) => {
    if (state[stateKey]) sp.set(urlKey, "1");
  });
  if (state.status && state.status !== DEFAULT_STATE.status) {
    sp.set("status", state.status);
  }
  if (state.sort && state.sort !== DEFAULT_STATE.sort)
    sp.set("sort", state.sort);
  if (!state.isAscending) sp.set("dir", "desc");
  if (state.page) sp.set("page", String(state.page));
  if (state.size && state.size !== DEFAULT_SIZE) {
    sp.set("size", String(state.size));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

/** Params for `venueAdminApi.list`; blanks and undefined are dropped by its query builder. */
export const toApiParams = (state) => ({
  page: state.page,
  size: state.size,
  sort: state.sort,
  isAscending: state.isAscending,
  status: state.status,
  id: state.id,
  name: state.name,
  street: state.street,
  city: state.city,
  county: state.county,
  postalCode: state.postalCode,
  timezone: state.timezone,
  country: state.country,
  state: state.country ? state.state : "",
  missingTimezone: state.missingTimezone ? true : undefined,
  missingMapLink: state.missingMapLink ? true : undefined,
});

/** The column-search values of `state`, as the drafts the search boxes edit. */
export const searchDrafts = (state) =>
  Object.fromEntries(SEARCH_KEYS.map((key) => [key, state[key] || ""]));

/** True when any filter differs from the default list (sort and paging excluded). */
export const hasFilters = (state) =>
  state.status !== DEFAULT_STATE.status ||
  Object.keys(FLAG_KEYS).some((key) => !!state[key]) ||
  Object.keys(URL_KEYS).some((key) => String(state[key] ?? "").trim() !== "");

/** Filter values reset to the defaults, keeping sort and page size. */
export const clearedFilters = (state) => ({
  ...DEFAULT_STATE,
  sort: state.sort,
  isAscending: state.isAscending,
  size: state.size,
});
